import React, {
  PropsWithChildren, useEffect, useRef, useState,
} from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/logo.svg';
import LegalPersonInfoModal from './LegalPersonInfoModal';
import LightFlow from './MainPage/LightFlow';
import PartnersMarquee from './MainPage/PartnersMarquee';
import './Layout.css';
import '../FlowExperience.css';

const navigation = [
  { label: 'Решения на базе ИИ', to: '/#AI' },
  { label: 'Разработка', to: '/#HardSkills' },
  { label: 'Как работаем', to: '/#Process' },
  { label: 'Сотрудничество', to: '/#Collaboration' },
];

const MainLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navigationRef = useRef<HTMLElement>(null);
  const initialNavigation = useRef(true);
  const location = useLocation();
  const isLegalPage = location.pathname !== '/';

  useEffect(() => {
    setIsMenuOpen(false);
    const behavior: ScrollBehavior = initialNavigation.current
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    initialNavigation.current = false;
    const frame = window.requestAnimationFrame(() => {
      if (location.hash) {
        const target = document.getElementById(location.hash.slice(1));
        if (target instanceof HTMLDetailsElement) {
          target.open = true;
        }
        target?.scrollIntoView({ behavior, block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.key, location.pathname, location.hash]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    navigationRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isMenuOpen]);

  return (
    <div className={`site-layout${isLegalPage ? '' : ' site-layout-immersive'}`}>
      {!isLegalPage && <LightFlow />}
      <a
        className="site-skip-link"
        href="#content"
      >
        Перейти к содержимому
      </a>
      <header className="site-header">
        <div className="site-container site-header-inner">
          <Link
            aria-label="A2Soft — на главную"
            className="site-logo"
            onClick={() => setIsMenuOpen(false)}
            to="/"
          >
            <Logo aria-hidden="true" />
          </Link>
          <nav
            ref={navigationRef}
            aria-label="Основная навигация"
            className={`site-navigation${isMenuOpen ? ' site-navigation-open' : ''}`}
            id="site-navigation"
          >
            {navigation.map(({ label, to }) => (
              <Link
                key={to}
                className="site-navigation-link"
                onClick={() => {
                  setIsMenuOpen(false);
                  if (isMenuOpen) {
                    menuButtonRef.current?.focus({ preventScroll: true });
                  }
                }}
                to={to}
              >
                {label}
              </Link>
            ))}
            <Link
              className="site-navigation-contact"
              onClick={() => setIsMenuOpen(false)}
              to="/#Contacts"
            >
              Обсудить проект
            </Link>
          </nav>
          <Link
            className="site-header-contact"
            to="/#Contacts"
          >
            Обсудить проект
          </Link>
          <button
            ref={menuButtonRef}
            aria-controls="site-navigation"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            className={`site-menu-toggle${isMenuOpen ? ' site-menu-toggle-open' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            type="button"
          >
            <span />
            <span />
          </button>
        </div>
      </header>
      <main
        className={isLegalPage ? 'site-content site-legal-content' : 'site-content'}
        id="content"
        tabIndex={-1}
      >
        <Outlet />
        {children}
      </main>
      <footer
        className="site-footer"
        id="Contacts"
      >
        <div className="footer-cta">
          <div
            className="site-container footer-cta-inner"
            data-flow-reveal
          >
            <div className="footer-cta-kicker">
              {!isLegalPage && (
                <>
                  <span className="section-index">05 /</span>
                  {' '}
                </>
              )}
              Обсудим проект
            </div>
            <div className="footer-cta-copy">
              <h2>Свяжитесь с нами.</h2>
              <p>Расскажите о задаче и о том, как работаете сейчас. Обсудим варианты, предварительный объём работ и следующий шаг.</p>
            </div>
            <div className="footer-contact-action">
              <a
                className="footer-contact-button"
                href="mailto:info@a2soft.ru"
              >
                <svg
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <rect
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    width="18"
                    x="3"
                    y="5"
                  />
                  <path
                    d="m4 7 8 6 8-6"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
                <span>Написать команде</span>
              </a>
              <a
                className="footer-email"
                href="mailto:info@a2soft.ru"
              >
                info@a2soft.ru
              </a>
            </div>
          </div>
        </div>
        {!isLegalPage && (
          <div className="site-container footer-partners">
            <PartnersMarquee />
          </div>
        )}
        <div className="site-container footer-information">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-accreditation footer-column">
                <h3>О компании</h3>
                <p>
                  Аккредитация Минцифры с 2019 г.
                  <span aria-hidden="true"> · </span>
                  № 10846
                </p>
                <button
                  onClick={() => setIsLegalModalOpen(true)}
                  type="button"
                >
                  Информация о юридическом лице
                </button>
              </div>
            </div>
            <div className="footer-office footer-column">
              <h3>Казанский офис</h3>
              <a
                className="footer-phone"
                href="tel:+79950958700"
              >
                +7 (995) 095 87 00
              </a>
              <a
                className="footer-address"
                href={`https://yandex.ru/maps/?text=${encodeURIComponent('Казань, улица Тукая, 23')}`}
                rel="noreferrer"
                target="_blank"
              >
                улица Тукая, д. 23, 2 этаж, Казань, 420021
              </a>
            </div>
            <div className="footer-documents footer-column">
              <h3>Документы</h3>
              <Link to="/privacy-policy">
                Политика обработки персональных данных
              </Link>
              <Link to="/user-agreement">
                Информированное согласие пользователя сайта
              </Link>
            </div>
          </div>
          <div className="footer-bottom">
            <span suppressHydrationWarning>
              ©
              {' '}
              {new Date().getFullYear()}
              {' '}
              A2Soft
            </span>
            <span className="footer-bottom-description">Решения на базе ИИ. Разработка. Интеграции.</span>
            <Link to="/#Header">
              Наверх
              <span aria-hidden="true">↑</span>
            </Link>
          </div>
        </div>
      </footer>
      <LegalPersonInfoModal
        isOpen={isLegalModalOpen}
        onClose={() => setIsLegalModalOpen(false)}
      />
    </div>
  );
};

export default MainLayout;
